import React, { useState } from "react";
import PropTypes from "prop-types";
import styled from "react-emotion";
import { injectStripe, Elements, CardElement } from "react-stripe-elements";
import { Modal, Button } from "appStyles";
import { purchaseSubscription } from "apis";
import { css } from "react-emotion";
import { FormControlLabel, Radio, RadioGroup } from "@material-ui/core";
import isLoading from "../../redux/reducers/isLoading";

const titleStyle = css`
  font-family: "Lucida Grande", "Lucida Sans Unicode", "Lucida Sans", Geneva,
    Verdana, sans-serif;
  font-size: 15px;
  line-height: 18px;
  margin-right: 10px;
  margin-top: 10px;
  margin-bottom: 10px;
`;

const SubmitButton = styled("button")`
  display: block;
  height: 40px;
  width: 100%;
  font-size: inherit;
  font-weight: bold;
  background-color: ${(props) => (props.disabled ? "#7795f8" : props.color)};
  border: none;
  box-shadow: 3px 3px 13px rgba(120, 120, 120, 0.5);
  font-family: "Lucida Grande", "Lucida Sans Unicode", "Lucida Sans", Geneva,
    Verdana, sans-serif;
  border-radius: 4px;
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};
  color: #fff;
  cursor: pointer;
  margin-top: 50px;
`;

const Container = styled("div")`
  * {
    box-sizing: border-box;
  }
  h1 {
    color: #32325d;
    font-weight: 400;
    line-height: 50px;
    font-size: 40px;
    margin: 20px 0;
    padding: 0;
  }
  .Checkout {
    margin: 0 auto;
    max-width: 800px;
    box-sizing: border-box;
    padding: 0 5px;
  }
  label {
    color: #6b7c93;
    font-weight: 300;
    letter-spacing: 0.025em;
  }
  button {
    white-space: nowrap;
    border: 0;
    outline: 0;
    display: inline-block;
    height: 40px;
    line-height: 40px;
    padding: 0 14px;
    box-shadow: 0 4px 6px rgba(50, 50, 93, 0.11), 0 1px 3px rgba(0, 0, 0, 0.08);
    color: #fff;
    border-radius: 4px;
    font-size: 15px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.025em;
    text-decoration: none;
    -webkit-transition: all 150ms ease;
    transition: all 150ms ease;
    margin-top: 10px;
  }
  form {
    margin-bottom: 40px;
    padding-bottom: 40px;
    /* border-bottom: 3px solid #e6ebf1; */
  }
  button:hover {
    color: #fff;
    cursor: pointer;
    background-color: #7795f8;
    transform: translateY(-1px);
    box-shadow: 0 7px 14px rgba(50, 50, 93, 0.1), 0 3px 6px rgba(0, 0, 0, 0.08);
  }
  input,
  .StripeElement {
    display: block;
    margin: 10px 0 20px 0;
    max-width: 790px;
    padding: 10px 14px;
    font-size: 1em;
    font-family: "Source Code Pro", monospace;
    box-shadow: rgba(50, 50, 93, 0.14902) 0px 1px 3px,
      rgba(0, 0, 0, 0.0196078) 0px 1px 0px;
    border: 0;
    outline: 0;
    border-radius: 4px;
    background: white;
  }
  input::placeholder {
    color: #aab7c4;
  }
  input:focus,
  .StripeElement--focus {
    box-shadow: rgba(50, 50, 93, 0.109804) 0px 4px 6px,
      rgba(0, 0, 0, 0.0784314) 0px 1px 3px;
    -webkit-transition: all 150ms ease;
    transition: all 150ms ease;
  }
  .StripeElement.IdealBankElement,
  .StripeElement.PaymentRequestButton {
    padding: 0;
  }
`;

const createOptions = (fontSize, padding) => {
  return {
    style: {
      base: {
        fontSize,
        color: "#424770",
        letterSpacing: "0.025em",
        fontFamily: "Source Code Pro, monospace",
        "::placeholder": {
          color: "#aab7c4",
        },
        ...(padding ? { padding } : {}),
      },
      invalid: {
        color: "#9e2146",
      },
    },
  };
};

function _CardForm({
  stripe,
  fontSize,
  children,
  billingInfo,
  couponId,
  history,
  loading,
  setLoading,
  plan,
  ...props
}) {
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const [terms, setTerms] = useState(false);
  const [commitment, setCommitment] = useState(false);
  const [makingCall, setMakingCall] = useState(false);

  const toggleAgreeToTerms = () => {
    setTerms(!terms);
  };

  const toggleAgreeToCommitment = () => {
    setCommitment(!commitment);
  };

  const handleChange = (e) => {
    setCommitment(e.target.value);
  };

  const appId = new URLSearchParams(window.location.search).get("appId");

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    // console.log(ev.complete);

    if (!stripe) return console.log("Stripe.js hasn't loaded yet.");
    const validBillingInfo = Object.keys(billingInfo).every((key) => {
      if (key !== "address_line2") {
        if (!billingInfo[key]) return false;
      }

      return true;
    });
    if (!validBillingInfo) return setError("Invalid card information");
    if (!terms || !commitment)
      return setError("Please agree to our terms and confirm a commitment.");
    setMakingCall(true);
    try {
      setLoading(true);
      const payload = await stripe.createToken(billingInfo);
      if (!payload.token) return setError("Invalid card information");
      await purchaseSubscription(
        payload.token.id,
        appId,
        couponId,
        plan.stripePriceId
      );
      setSuccess(true);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
      setMakingCall(false);
    }
  };

  const renderSuccessModal = () => {
    if (!success) return null;
    return (
      <div
        style={{
          height: "100%",
          width: "100%",
          background: "#cbcbcb8a",
          position: "fixed",
          zIndex: 1,
          top: 0,
          left: 0,
        }}
      >
        <Modal hidden={true}>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              flexDirection: "column",
            }}
          >
            <p>
              Congrats, your app is now live! Send your app download link or QR
              code to your customers and watch your business grow. Pro tip -
              incentivize your customers to download through exclusive content
              only offered in your app. Plus, keep them coming back by sending
              push notifications!
            </p>
            <Button onClick={() => history.push("/lobby")}>
              Great! Let’s Get Started!
            </Button>
          </div>
        </Modal>
      </div>
    );
  };

  const renderErrorModal = () => {
    if (!error) return null;
    return (
      <div
        style={{
          height: "100%",
          width: "100%",
          background: "#cbcbcb8a",
          position: "fixed",
          zIndex: 1,
          top: 0,
          left: 0,
        }}
      >
        <Modal hidden={true}>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              flexDirection: "column",
            }}
          >
            <p>{`Something went wrong with subscription: ${error}`}</p>
            <Button onClick={() => history.push("/lobby")}>Go to Lobby</Button>
            <Button onClick={() => setError(false)}>Try Again</Button>
          </div>
        </Modal>
      </div>
    );
  };

  const buttonControl = !terms || !commitment;
  // console.dir(billingInfo);
  // console.log("disabled: " + buttonControl);
  const [cardFormComplete, setCardFormComplete] = useState(false);
  // Handle real-time validation errors from the card Element.
  const handleChangeCardElement = (event) => {
    // console.dir(event);
    if (event.error) {
      setError(event.error.message);
    } else {
      setError(null);
    }
    setCardFormComplete(event.complete);
  };
  return (
    <form onSubmit={handleSubmit}>
      {renderSuccessModal()}
      {renderErrorModal()}
      <CardElement
        {...createOptions(fontSize)}
        onChange={handleChangeCardElement}
      />
      <div
        className={titleStyle}
        style={{ display: "flex", alignItems: "center" }}
      >
        <input
          type="checkbox"
          onChange={toggleAgreeToTerms}
          checked={terms}
          style={{ margin: "0px", marginRight: "15px" }}
        />
        <span className={titleStyle}>
          By continuing, I agree to the{" "}
          <a style={{ cursor: "pointer" }} href="/terms" target="_blank">
            Terms of Conditions (TOS)
          </a>{" "}
          and{" "}
          <a
            style={{ cursor: "pointer" }}
            href="/privacy_policy"
            target="_blank"
          >
            Privacy Policy
          </a>
          , including responsibilities of owning a mobile app, customer
          management, and cancellation policy.
        </span>
      </div>
      <div
        className={titleStyle}
        style={{ display: "flex", alignItems: "center" }}
      >
        <input
          type="checkbox"
          onChange={toggleAgreeToCommitment}
          checked={commitment}
          style={{ margin: "0px", marginRight: "15px" }}
        />
        <span className={titleStyle}>
          I agree to paying my subscription commitment in full. I understand
          Mobile360 reserves the right to submit delinquent payments to
          collections agencies, which could adversely effect my credit score.
        </span>
      </div>
      {/* <RadioGroup
        aria-label="commitment"
        name="commitment"
        value={commitment}
        onChange={handleChange}
      >
        <FormControlLabel
          value={"6MONTH"}
          control={<Radio />}
          label="I agree to a 6 month commitment."
        />
        <FormControlLabel
          value={"1YEAR"}
          control={<Radio />}
          label="I agree to a 1 year commitment."
        />
        <FormControlLabel
          value={"2YEAR"}
          control={<Radio />}
          label="I agree to a 2 year commitment."
        />
      </RadioGroup> */}

      {children}
      <SubmitButton
        // style={{
        //   width: "100%",
        //   fontSize: "1.5em",
        //   // background: loading ? "grey" : "#89d2d3",
        //   // pointerEvents: loading ? "none" : "auto",
        // }}
        color="#89d2d3"
        disabled={buttonControl || !cardFormComplete || makingCall}
      >
        Launch my Mobile App!
      </SubmitButton>
    </form>
  );
}

const CardForm = injectStripe(_CardForm);

function StripeCardForm({ style, ...props }) {
  return (
    <div
      style={{
        height: "50%",
        marginTop: "20px",
        ...style,
      }}
    >
      <Container>
        <Elements>
          <CardForm {...props} />
        </Elements>
      </Container>
    </div>
  );
}

export default StripeCardForm;

StripeCardForm.propTypes = {
  billingInfo: PropTypes.object.isRequired,
};
